import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, ExternalLink } from 'lucide-react';
import { useCMS } from '../context/CMSContext';
import { successStories as defaultStories } from '../data/mockData';

const FADE_UP = { opacity: 0, y: 20 };
const VISIBLE = { opacity: 1, y: 0 };

const FeaturedStoryCard = ({ story, index }) => (
  <motion.article
    initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
    transition={{ delay: index * 0.08, duration: 0.7 }}
    className="group relative overflow-hidden rounded-2xl border border-white/5 hover:border-white/20 transition-colors duration-500"
    data-testid={`featured-story-${story.slug}`}
  >
    <Link to="/our-work" className="block">
      <div className="relative aspect-[16/9] overflow-hidden">
        <img src={story.image} alt={`${story.client} — ${story.title}`}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-105"
          loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050914] via-[#050914]/30 to-transparent" />
        {story.liveUrl && (
          <div className="absolute top-5 right-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.2em] text-white font-bold bg-[#FF6B35]/90 backdrop-blur">
            <ExternalLink className="w-3 h-3" /> Live Preview
          </div>
        )}
      </div>
      <div className="p-8 lg:p-10">
        <div className="text-[11px] uppercase tracking-[0.25em] text-white/50 font-bold mb-3">
          {story.industry} · {story.client}
        </div>
        <h3 className="font-display text-2xl lg:text-4xl font-medium tracking-tight text-white leading-snug max-w-3xl">
          {story.title}
        </h3>
        <p className="mt-4 text-white/55 text-base lg:text-lg font-light leading-relaxed max-w-3xl">
          {story.summary}
        </p>
        <div className="mt-7 inline-flex items-center gap-2 text-white text-sm font-semibold">
          <span className="border-b border-white/30 group-hover:border-white pb-0.5 transition-colors">View case study</span>
          <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
        </div>
      </div>
    </Link>
  </motion.article>
);

const FeaturedStoriesHome = () => {
  const { get } = useCMS();
  const stories = (get('success_stories') || defaultStories).slice(0, 1);

  return (
    <section className="relative py-24 lg:py-32 bg-[#050914] border-t border-white/5" data-testid="featured-stories-home">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <motion.p initial={FADE_UP} whileInView={VISIBLE} viewport={{ once: true }}
            className="inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-white/50 font-bold mb-6">
            <span className="w-8 h-px bg-white/30" /> Featured Work
          </motion.p>
          <motion.h2 initial={FADE_UP} whileInView={VISIBLE} viewport={{ once: true }} transition={{ delay: 0.1 }}
            className="font-display text-3xl md:text-4xl lg:text-5xl font-medium tracking-tighter text-white leading-[1.05]">
            Selected projects, shipped with intent.
          </motion.h2>
        </div>
        <div className="grid grid-cols-1 gap-6">
          {stories.map((s, i) => <FeaturedStoryCard key={s.id} story={s} index={i} />)}
        </div>
      </div>
    </section>
  );
};

export default FeaturedStoriesHome;
